package com.dotdrives.repository

import com.dotdrives.client.DonationClient
import com.dotdrives.model.AttentionItem
import com.dotdrives.model.Donation
import com.dotdrives.model.DonationSmartDefaults
import com.dotdrives.model.GivingStatement
import com.dotdrives.model.GivingStatementBatchSummary
import com.dotdrives.model.GivingStatementDraft
import com.dotdrives.model.NewDonation
import com.dotdrives.model.Target
import kotlin.time.Instant

interface DonationRepository {
    suspend fun targets(orgId: String): Result<List<Target>>

    /**
     * `occurredAt` bounds are inclusive on both ends. `query` matches giver display name or
     * memo. Returns every visible row including reversals (negative `amount`) — callers net,
     * never sum absolute values.
     */
    suspend fun list(
        orgId: String,
        occurredFrom: Instant,
        occurredTo: Instant,
        query: String?,
        targetId: String?,
        source: String?,
    ): Result<List<Donation>>

    suspend fun get(orgId: String, donationId: String): Result<Donation>

    /** All queue entries, both categories combined — see [AttentionItem]. */
    suspend fun attentionQueue(orgId: String, asOf: Instant): Result<List<AttentionItem>>

    suspend fun smartDefaults(orgId: String, giverId: String): Result<DonationSmartDefaults>

    suspend fun record(orgId: String, donation: NewDonation): Result<Donation>

    /** Per-giver totals for `year`, aggregated from `donations` client-side (no aggregate RPC
     * exists in this schema). `alreadyGeneratedGiverIds` lets the caller skip a giver whose
     * `giving_statements` row already exists — the table has no update grant, only insert. */
    suspend fun statementBatchSummary(orgId: String, year: Int): Result<GivingStatementBatchSummary>

    /** `drafts` must already be the caller's already-filtered pending set (no existing
     * statement for this giver/year) -- see [DonationClient.generateStatements]. */
    suspend fun generateStatements(
        orgId: String,
        year: Int,
        drafts: List<GivingStatementDraft>,
    ): Result<List<GivingStatement>>
}

class SupabaseDonationRepository(
    private val client: DonationClient = DonationClient(),
) : DonationRepository {
    override suspend fun targets(orgId: String): Result<List<Target>> =
        runCatching { client.targets(orgId) }

    override suspend fun list(
        orgId: String,
        occurredFrom: Instant,
        occurredTo: Instant,
        query: String?,
        targetId: String?,
        source: String?,
    ): Result<List<Donation>> =
        runCatching { client.list(orgId, occurredFrom, occurredTo, query, targetId, source) }

    override suspend fun get(orgId: String, donationId: String): Result<Donation> =
        runCatching { client.get(orgId, donationId) }

    override suspend fun attentionQueue(orgId: String, asOf: Instant): Result<List<AttentionItem>> =
        runCatching { client.attentionQueue(orgId, asOf) }

    override suspend fun smartDefaults(orgId: String, giverId: String): Result<DonationSmartDefaults> =
        runCatching { client.smartDefaults(orgId, giverId) }

    override suspend fun record(orgId: String, donation: NewDonation): Result<Donation> =
        runCatching { client.record(orgId, donation) }

    override suspend fun statementBatchSummary(orgId: String, year: Int): Result<GivingStatementBatchSummary> =
        runCatching { client.statementBatchSummary(orgId, year) }

    override suspend fun generateStatements(
        orgId: String,
        year: Int,
        drafts: List<GivingStatementDraft>,
    ): Result<List<GivingStatement>> =
        runCatching { client.generateStatements(orgId, year, drafts) }
}
